import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

import Navbar from "./components/NavBar/NavBar.jsx";
import PermissionRoute from "./components/PermissionRoutes/PermissionRoutes";

import Home from "./pages/Home/Home";
import Login from "./pages/Login/Login";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import AllTablePage from "./pages/AllTablePage/AllTablePage.jsx";
import AllEditPage from "./pages/AllEditPage/AllEditPage.jsx";
import AllFormPage from "./pages/AllFormPage/AllFormPage.jsx";
import ProjectDashboard from "./pages/ProjectDashboard/ProjectDashboard.jsx";
import About from "./pages/About/About";
import Dashboard from "./pages/Dashboard/Dashboard";
import Settings from "./pages/Settings/Settings";
import DocViewer from "./pages/DocViewer/DocViewer.jsx";
import StaffPerformanceDashboard from "./pages/EmployeeRating/EmployeeRating.js";
import UAUCManagementDashboard from "./components/DashboardsSingle/DashboardsSingle.js";
import LabourRec from "./pages/LabourRec/LabourRec.js";
import SessionTimeout from "./components/SessionTimeout/SessionTimeout.jsx";

import { getAppConfig } from "./utils/config.js";
import RegisterUsers from "./pages/RegisterUsers/RegisterUsers.js";
import Profile from "./pages/Profile/Profile.js";
import FilesManagement from "./pages/FilesManagement/FilesManagement.js";
import Error404 from "./pages/404Error/404Error.js";
import Management from "./pages/Management/Management.js";
import Testing from "./pages/testingui/testingUi.jsx";

const { companyName, appName, logo } = getAppConfig();



/* ✅ Layout wrapper that conditionally hides Navbar based on route */
/* Layout wrapper with fixed 200px sidebar */


const PrivateLayout = ({ children }) => {
  const location = useLocation();

  const hideNavbar = /\/edit\/LinkUaUcExecution/i.test(location.pathname);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    return parseInt(localStorage.getItem("sidebarWidth")) || 220;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const width = parseInt(localStorage.getItem("sidebarWidth")) || 220;
      setSidebarWidth(width);
    }, 150);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="app-layout">
      {/* ✅ SESSION TIMEOUT HERE */}
      <SessionTimeout />

      {!hideNavbar && (
        <div className="sidebar-wrapper" style={{ width: sidebarWidth }}>
          <Navbar />
        </div>
      )}

      <main
        className={hideNavbar ? "no-navbar-page" : "content-wrapper"}
        style={{
          marginLeft: hideNavbar ? 0 : sidebarWidth,
          width: hideNavbar ? "100%" : `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        {children}
      </main>
    </div>
  );
};


const App = () => {
  const privateRoutes = [
    { path: "/", component: <Home /> },
    { path: "/test", component: <Testing /> },
    {
      path: "/management",
      component: <Management />,
      permission: "management:view",
    },
    {
      path: "/project-dashboard",
      permission: "projectDashboard:view",
      component: <ProjectDashboard />,
    },
    {
      path: "/table/:model",
      permission: "table:view",
      component: <AllTablePage />,
    },
    {
      path: "/edit/:model/:id",
      permission: "table:edit",
      component: <AllEditPage />,
    },
    {
      path: "/form/:model",
      permission: "table:create",
      component: <AllFormPage />,
    },
    {
      path: "/dashboard",
      permission: "dashboard:view",
      component: <Dashboard />,
    },
    { path: "/settings", permission: "settings:view", component: <Settings /> },
    {
      path: "/rating",
      permission: "rating.read",
      component: <StaffPerformanceDashboard />,
    },
    {
      path: "/labourRec",
      permission: "labourRec.read",
      component: <LabourRec />,
    },
    { path: "/about", permission: "about:view", component: <About /> },
    { path: "/profile", permission: "profile:view", component: <Profile /> },
    {
      path: "/files",
      permission: "FilesManagement:view",
      component: <FilesManagement />,
    },
  ];

  // 🔄 Branding setup
  useEffect(() => {
    if (appName) document.title = appName;
    if (logo) {
      const favicon = document.getElementById("favicon");
      if (favicon) favicon.href = logo;
    }
  }, []);

  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3000} />
      <Routes>
        {/* 🔓 Public Routes */}
        <Route
          path="/login"
          element={
            <PermissionRoute isPublic>
              <Login />
            </PermissionRoute>
          }
        />
        <Route
          path="/uitesting"
          element={
            <PermissionRoute isPublic>
              <Testing/>
            </PermissionRoute>
          }
        />

        <Route
          path="/register"
          element={
            <PermissionRoute isPublic>
              <RegisterUsers />
            </PermissionRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PermissionRoute isPublic>
              <ResetPassword />
            </PermissionRoute>
          }
        />

        {/* 🔐 Private Routes */}
        {privateRoutes.map(({ path, component, permission, noLayout }) => (
          <Route
            key={path}
            path={path}
            element={
              <PermissionRoute permission={permission}>
                {noLayout ? (
                  component
                ) : (
                  <PrivateLayout>{component}</PrivateLayout>
                )}
              </PermissionRoute>
            }
          />
        ))}

        {/* 🚨 Fallback 404 (NO NAVBAR) */}
        <Route path="*" element={<Error404 />} />
      </Routes>
    </Router>
  );
};

export default App;
